import { css, html, LitElement } from 'lit';
import '../../library/fields/d-expansion.js';

import '../../library/editors/elements/d-select-dropdown.js';
import '../../library/elements/d-section.js';
import '../../library/editors/elements/d-edit-text.js';
import { professionsForHealthNormalized } from 'src/store/selectors';
import { customElement, property, state } from 'lit/decorators.js';
import type { AccountStateEmployeeViewModelAccessLevelEnum } from '@trinnvis/dabih-api-client';
import { produce } from 'immer';
import type { PrepareEmployeeRowItem } from './prepare-employee-row-item';
import type { SelectDropdownOption } from '../../library/editors/elements/select-dropdown-option';
import { isEmailValid } from '../../store/selectors/utilities/isEmailValid';
import { t } from 'src/i18n';

/**
 *
 *
 */
@customElement('d-prepare-employee-row')
export class DPrepareEmployeeRow extends LitElement {
  static shadowRootOptions = { ...LitElement.shadowRootOptions, delegatesFocus: true };
  static readonly styles = css`
    :host {
      display: flex;
    }

    .personBox {
      flex: 1;
      padding: 0 14px 12px 16px;
      background: hsla(0, 0%, 100%, 0.2);
      margin: 8px 0;
      border-radius: 14px;
    }

    d-section {
      border: none;
    }
    .personRow d-edit-text,
    .personRow d-prepare-select {
      min-width: 260px;
    }

    d-section > * {
      padding-top: 8px;
      padding-bottom: 6px;
    }

    d-section > * {
      font-family: var(--mainSans);
    }

    .minWidth300 {
      min-width: 300px;
      flex: 1;
    }

    d-edit-text,
    d-select-dropdown {
      border: none;
      box-shadow: none;
    }
  `;
  @property({ type: Number })
  index = 0;
  @property({ type: Object })
  item!: PrepareEmployeeRowItem;
  @property({ type: Boolean })
  hideProfessions = false;
  @property({ type: Array })
  professions: string[] = [];
  @property({ type: Array })
  associationTypes: string[] = [];
  @state()
  private nameFocused = false;
  private accessLevelOptions: SelectDropdownOption[] = [
    { value: 'NONE', text: t('prepareEmployee.accessLevelNone') },
    { value: 'READONLY', text: t('prepareEmployee.accessLevelReadonly') },
    { value: 'USER', text: t('prepareEmployee.accessLevelFull') },
  ];

  private get professionOptions(): SelectDropdownOption[] {
    // Use provided professions if available, otherwise fall back to health professions
    const professions = this.professions.length > 0 ? this.professions : professionsForHealthNormalized;
    const result: SelectDropdownOption[] = [];
    professions.forEach(function (item) {
      result.push({ value: item, text: item });
      if (item === 'Fysioterapeut') {
        result.push({ value: '- Manuellterapeut', text: '- Manuellterapeut' });
      }
    });
    return result;
  }

  private get associationTypeOptions(): SelectDropdownOption[] {
    return this.associationTypes.map((s) => ({ value: s, text: s }));
  }

  _stringIsEmpty(value: string) {
    return value ? value.trim().length === 0 : true;
  }

  _isEmail(email) {
    return !this._stringIsEmpty(email) && isEmailValid(email);
  }

  _isAccessLevelDisabled(index, email) {
    return index === 0 || !this._isEmail(email);
  }

  _isDisabled(index) {
    return index === 0;
  }

  render() {
    return html`
      <div class="personBox">
        <d-section>
          <d-edit-text
            .disabled=${this._isDisabled(this.index)}
            class="minWidth300"
            .value=${this.item.firstName}
            label=${t('prepareEmployee.firstNameLabel')}
            select-on-focus
            outskirts
            system-content
            @value-changed=${(e: CustomEvent<{ value: string }>) => {
              this.item = produce(this.item, (draft) => {
                draft.firstName = e.detail.value;
              });
              this.dispatchEvent(
                new CustomEvent<{ item: PrepareEmployeeRowItem }>('employee-changed', { detail: { item: this.item } }),
              );
            }}
          >
          </d-edit-text>
          <d-edit-text
            .disabled=${this._isDisabled(this.index)}
            class="minWidth300"
            .value=${this.item.lastName}
            label=${t('prepareEmployee.lastNameLabel')}
            select-on-focus
            system-content
            outskirts
            @value-changed=${(e: CustomEvent<{ value: string }>) => {
              this.item = produce(this.item, (draft) => {
                draft.lastName = e.detail.value;
              });
              this.dispatchEvent(
                new CustomEvent<{ item: PrepareEmployeeRowItem }>('employee-changed', { detail: { item: this.item } }),
              );
            }}
          >
          </d-edit-text>
        </d-section>
        <d-section>
          <d-edit-text
            label=${t('prepareEmployee.emailLabel')}
            .disabled=${this._isDisabled(this.index)}
            class="minWidth300"
            placeholder=${t('prepareEmployee.emailPlaceholder')}
            type="email"
            .value=${this.item.email}
            select-on-focus
            system-content
            outskirts
            @value-changed=${(e: CustomEvent<{ value: string }>) => {
              this.item = produce(this.item, (draft) => {
                draft.email = e.detail.value;
              });
              this.dispatchEvent(
                new CustomEvent<{ item: PrepareEmployeeRowItem }>('employee-changed', {
                  detail: { item: this.item },
                }),
              );
            }}
          >
          </d-edit-text>
          <d-select-dropdown
            .options=${this.accessLevelOptions}
            class="minWidth300"
            .disabled=${this._isAccessLevelDisabled(this.index, this.item.email)}
            field="employees_accessLevel"
            .value=${this.item.accessLevel}
            select-on-focus
            system-content
            outskirts
            @value-changed=${(e: CustomEvent<{ value: string }>) => {
              this.item = produce(this.item, (draft) => {
                draft.accessLevel = e.detail.value as AccountStateEmployeeViewModelAccessLevelEnum;
              });
              this.dispatchEvent(
                new CustomEvent<{ item: PrepareEmployeeRowItem }>('employee-changed', {
                  detail: { item: this.item },
                }),
              );
            }}
          >
          </d-select-dropdown>
        </d-section>
        <d-section>
          ${this.hideProfessions
            ? html`<d-edit-text
                label=${t('prepareEmployee.professionTitleLabel')}
                class="minWidth300"
                .value=${this.item.profession}
                system-content
                outskirts
                @value-changed=${(e: CustomEvent<{ value: string }>) => {
                  this.item = produce(this.item, (draft) => {
                    draft.profession = e.detail.value;
                  });
                  this.dispatchEvent(
                    new CustomEvent<{ item: PrepareEmployeeRowItem }>('employee-changed', {
                      detail: { item: this.item },
                    }),
                  );
                }}
              >
              </d-edit-text>`
            : html`<d-select-dropdown
                .options=${this.professionOptions}
                label=${t('prepareEmployee.professionLabel')}
                class="minWidth300"
                placeholder=${t('prepareEmployee.professionPlaceholder')}
                .value=${this.item.profession}
                system-content
                outskirts
                @value-changed=${(e: CustomEvent<{ value: string }>) => {
                  if (e.detail.value === 'LIS 1') {
                    this.item = produce(this.item, (draft) => {
                      draft.profession = e.detail.value;
                      draft.associationType = 'LIS 1';
                    });
                  } else {
                    this.item = produce(this.item, (draft) => {
                      draft.profession = e.detail.value;
                    });
                  }
                  this.dispatchEvent(
                    new CustomEvent<{ item: PrepareEmployeeRowItem }>('employee-changed', {
                      detail: { item: this.item },
                    }),
                  );
                }}
              >
              </d-select-dropdown>`}

          <d-select-dropdown
            .options=${this.associationTypeOptions}
            label=${t('prepareEmployee.associationTypeLabel')}
            class="minWidth300"
            placeholder=${t('prepareEmployee.associationTypePlaceholder')}
            value="${this.item.associationType}"
            system-content
            outskirts
            @value-changed=${(e: CustomEvent<{ value: string }>) => {
              this.item = produce(this.item, (draft) => {
                draft.associationType = e.detail.value;
              });
              this.dispatchEvent(
                new CustomEvent<{ item: PrepareEmployeeRowItem }>('employee-changed', {
                  detail: { item: this.item },
                }),
              );
            }}
          >
          </d-select-dropdown>
        </d-section>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-prepare-employee-row': DPrepareEmployeeRow;
  }
}
